
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useBookings, useCancelBooking } from '@/api/classes';
import type { GymClass } from '@/api/classes';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirmationStore } from '@/lib/stores/confirmation-store';

interface BookingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
  timeSlotId: string;
}

export function BookingsDialog({
  open,
  onOpenChange,
  gymClass,
  timeSlotId,
}: BookingsDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirmationStore = useConfirmationStore();

  const timeSlot = gymClass.timeSlots.find(
    (slot) => (slot.timeSlotId || slot._id) === timeSlotId
  );

  // For one-time slots, use the slot's date. For recurring slots, allow date selection
  const [selectedDate, setSelectedDate] = useState<string>(
    timeSlot?.date || format(new Date(), 'yyyy-MM-dd')
  );

  const { data: bookings, isLoading } = useBookings(
    gymClass._id,
    timeSlotId,
    selectedDate
  );
  const cancelBookingMutation = useCancelBooking();

  const handleCancelBooking = (bookingId: string) => {
    confirmationStore.open({
      title: t('classes.confirmCancelBooking'),
      message: t('classes.confirmCancelBooking'),
      variant: 'destructive',
      confirmLabel: t('common.cancel'),
      onConfirm: async () => {
        try {
          await cancelBookingMutation.mutateAsync({
            classId: gymClass._id,
            bookingId,
          });
          toast({
            title: t('common.success'),
            description: t('classes.bookingCancelled'),
          });
        } catch (error: any) {
          toast({
            title: t('common.error'),
            description:
              error.response?.data?.message || t('common.errorOccurred'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('classes.bookings')}</DialogTitle>
          <DialogDescription>
            {gymClass.name} -{' '}
            {timeSlot && `${timeSlot.startTime} - ${timeSlot.endTime}`}
            {timeSlot?.applyEveryDay && (
              <span className="ml-2 text-xs">
                ({t('classes.recurring', 'Recurring')})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {timeSlot?.applyEveryDay && (
          <div className="space-y-2">
            <Label htmlFor="date">
              {t('classes.selectDate', 'Select Date')}
            </Label>
            <Input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !bookings || bookings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('classes.noBookings')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('classes.fullName')}</TableHead>
                <TableHead>{t('classes.phoneNumber')}</TableHead>
                <TableHead>{t('classes.status')}</TableHead>
                <TableHead>{t('common.date')}</TableHead>
                <TableHead className="text-right">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell className="font-medium">
                    {booking.fullName}
                  </TableCell>
                  <TableCell>{booking.phoneNumber || '-'}</TableCell>
                  <TableCell>
                    {booking.status === 'active' ? (
                      <Badge variant="default">{t('classes.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">
                        {t('classes.cancelled')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {booking.createdAt &&
                      format(new Date(booking.createdAt), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell className="text-right">
                    {booking.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelBooking(booking._id)}
                      >
                        {t('classes.cancelBooking')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
